import {
  Input,
  InputNumber,
  Tooltip,
  Upload,
  message,
  Select,
  DatePicker,
  Popover,
  Button,
} from 'antd';
import InputMask from 'react-input-mask';
import { toastr } from 'react-redux-toastr';
import moment from 'moment-timezone';
import React, { useEffect, useRef, useState } from 'react';
import {
  CheckBullet,
  CopyOutlineIcon,
  PasteOutlineIcon,
  PlusIcon,
  TrashOutlineIcon,
} from 'assets/icon/dynamic';

const SGT_TZ = 'Asia/Singapore';
const DEFAULT_DAYS = [
  { name: 'Sunday', alias: 'Sun', isActive: false, hours: [['00:00', '23:59']] },
  { name: 'Monday', alias: 'Mon', isActive: false, hours: [['00:00', '23:59']] },
  { name: 'Tuesday', alias: 'Tue', isActive: false, hours: [['00:00', '23:59']] },
  { name: 'Wednesday', alias: 'Wed', isActive: false, hours: [['00:00', '23:59']] },
  { name: 'Thursday', alias: 'Thu', isActive: false, hours: [['00:00', '23:59']] },
  { name: 'Friday', alias: 'Fri', isActive: false, hours: [['00:00', '23:59']] },
  { name: 'Saturday', alias: 'Sat', isActive: false, hours: [['00:00', '23:59']] },
];
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  fetchMediaCategories,
  createMediaCategory,
  setMediaCategory,
} from 'state/actions/mediaCategories';
import { fetchTags, createTag, setTag } from 'state/actions/tag';
import { regexDescription, regexInput } from 'utils/regex';
import { imgUrl } from 'utils/url';
import { log } from 'utils';
import {
  setMedia,
  createMedia,
  modifyMedia,
} from 'state/actions/media';
import { TrashIcon } from 'assets/icon/dynamic';
import ModalMediaCategoryForm from 'components/ModalMediaCategoryForm';
import ModalTagForm from 'components/ModalTagForm';
import './MediaForm.css';
import './MediaForm.scss';
import '../ModalSchedulingForm/ModalSchedulingForm.css';
import { ImportIcon } from 'assets/icon';

function MediaForm({ action, history, id, error, isEditing }) {
  const {
    loading,
    userData,
    media,
    categoriesList,
    tagsList,
    categoryDraft,
    tagDraft,
  } = useSelector(
    (state) => ({
      loading: state.media.loading,
      userData: state.auth.userData,
      categoriesList: state.mediaCategories.list,
      tagsList: state.tags.list,
      categoryDraft: state.mediaCategories.mediaCategory,
      tagDraft: state.tags.tag,
      media: state.media.media,
    }),
    shallowEqual
  );

  const [isDragged, setIsDragged] = useState(false);
  const { siteId, siteName } = useParams();
  const [errors, setErrors] = useState(error);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [role, setRole] = useState(null);
  const [allDay, setAllDay] = useState(
    media?.dayMode ? media.dayMode === 'allDay' : true
  );
  const [days, setDays] = useState(() =>
    DEFAULT_DAYS.map((d) => ({ ...d, hours: d.hours.map((h) => [...h]) }))
  );
  const [isCopied, setIsCopied] = useState();
  const [copyTime, setCopyTime] = useState();
  const normalizeIds = (arr) =>
    Array.isArray(arr)
      ? arr
          .map((v) =>
            typeof v === 'string' ? v : v?._id || v?.id || null
          )
          .filter(Boolean)
      : [];

  const [tags, setTags] = useState(normalizeIds(media?.tagIds));
  const [categoryIds, setCategoryIds] = useState(
    Array.isArray(media?.categoryIds)
      ? normalizeIds(media.categoryIds)
      : media?.categoryId
      ? [media.categoryId]
      : []
  );
  const [isModalCategoryShow, setIsModalCategoryShow] = useState(false);
  const [isModalTagShow, setIsModalTagShow] = useState(false);
  const videoRef = useRef(null);

  const dispatch = useDispatch();

  useEffect(() => {
    setErrors(error);
  }, [error]);

  useEffect(() => {
    if (siteId) {
      dispatch(fetchMediaCategories({ siteId }));
      dispatch(fetchTags({ siteId }));
    }
  }, [siteId, dispatch]);

  useEffect(() => {
    if (Array.isArray(media?.categoryIds)) {
      setCategoryIds(normalizeIds(media.categoryIds));
    } else if (media?.categoryId) {
      setCategoryIds([media.categoryId]);
    }
  }, [media?.categoryIds, media?.categoryId]);

  useEffect(() => {
    setImagePreviewUrl((prev) => {
      if (!prev) {
        return media.file
          ? media.file && URL.createObjectURL(media.file)
          : media.logo &&
              (media.logo.startsWith('http')
                ? media.logo
                : `${imgUrl}/media/${media.logo}`);
      }
      return prev;
    });
  }, [media]);

  useEffect(() => {
    if (Array.isArray(media?.tagIds)) setTags(normalizeIds(media.tagIds));
  }, [media?.tagIds]);

  useEffect(() => {
    if (media?.dayMode) setAllDay(media.dayMode === 'allDay');
  }, [media?.dayMode]);

  useEffect(() => {
    const sched = Array.isArray(media?.schedulingIds) && media.schedulingIds[0];
    if (!sched) return;
    const patch = {};
    if (sched.start && !media.start) patch.start = sched.start;
    if (sched.end && !media.end) patch.end = sched.end;
    if (Array.isArray(sched.days) && !media.days) patch.days = sched.days;
    if (sched.type) {
      const mode = sched.type === 'specificDays' ? 'customDay' : 'allDay';
      if (!media.dayMode) patch.dayMode = mode;
    }
    if (Object.keys(patch).length > 0) {
      dispatch(setMedia({ ...media, ...patch }));
    }
  }, [media?.schedulingIds]); // eslint-disable-line

  useEffect(() => {
    if (Array.isArray(media?.days) && media.days.length > 0) {
      setAllDay(false);
      setDays((prev) => {
        const next = prev.map((d) => ({
          ...d,
          isActive: false,
          hours: [['00:00', '23:59']],
        }));
        media.days.forEach((day) => {
          const ind = next.findIndex((d) => d.name === day.day);
          if (ind === -1) return;
          next[ind].isActive = true;
          next[ind].hours = (day.hours || []).map((h) =>
            typeof h === 'string' ? h.split('-') : h
          );
        });
        return next;
      });
    }
  }, [media?.days]);

  const onFileChangedHandler = (file) => {
    const validTypes = [
      'image/jpeg',
      'image/png',
      'video/mp4',
      'video/quicktime',
    ];
    const maxFileSize = 300 * 1024 * 1024;
    if (validTypes.includes(file.type) && file.size <= maxFileSize) {
      dispatch(setMedia({ ...media, file }));
    } else {
      message.error('Invalid file type or size exceeds 300MB');
    }
    setIsDragged(false);
  };

  const handleDeletePreviewImage = () => {
    setImagePreviewUrl(null);
  };

  const handleChange = (name, value) => {
    dispatch(setMedia({ ...media, [name]: value }));
  };

  const handleChangeHour = (name, index, indexAgain, value) => {
    const data = [...days];
    if (name === 'from') {
      data[index].hours[indexAgain] = [value, data[index].hours[indexAgain][1]];
    } else if (name === 'to') {
      data[index].hours[indexAgain] = [data[index].hours[indexAgain][0], value];
    }
    if (
      data[index].hours[indexAgain][0].replace(/[^a-zA-Z0-9]/g, '').length === 4 &&
      data[index].hours[indexAgain][1].replace(/[^a-zA-Z0-9]/g, '').length === 4
    ) {
      if (data[index].hours[indexAgain][1] < data[index].hours[indexAgain][0]) {
        data[index].hours[indexAgain] = [
          data[index].hours[indexAgain][1],
          data[index].hours[indexAgain][0],
        ];
      }
      const before = data[index].hours[indexAgain][0];
      const after = data[index].hours[indexAgain][1];
      if (parseInt(before.substring(0, 2), 10) > 23) {
        data[index].hours[indexAgain][0] = data[index].hours[indexAgain][0].replace(/^.{2}/g, '23');
      }
      if (parseInt(before.substring(3), 10) > 59) {
        data[index].hours[indexAgain][0] = data[index].hours[indexAgain][0].replace(/.{0,2}$/, '59');
      }
      if (parseInt(after.substring(0, 2), 10) > 23) {
        data[index].hours[indexAgain][1] = data[index].hours[indexAgain][1].replace(/^.{2}/g, '23');
      }
      if (parseInt(after.substring(3), 10) > 59) {
        data[index].hours[indexAgain][1] = data[index].hours[indexAgain][1].replace(/.{0,2}$/, '59');
      }
      data[index].hours.sort(
        (a, b) =>
          parseInt(a[0].replace(/[^a-zA-Z0-9]/g, ''), 10) -
          parseInt(b[0].replace(/[^a-zA-Z0-9]/g, ''), 10)
      );
    }
    setDays([...data]);
  };

  const handleDeleteHourDay = (index, indexAgain) => {
    const newDay = [...days];
    if (newDay[index].hours.length > 1) {
      newDay[index].hours = newDay[index].hours.filter((_, ind) => ind !== indexAgain);
    } else {
      newDay[index].hours = [];
    }
    setDays([...newDay]);
  };

  const handleDeleteDay = (index) => {
    const newDay = [...days];
    newDay[index].hours = [];
    setDays(newDay);
  };

  const handleActiveDay = (index) => {
    const newDay = [...days];
    newDay[index].isActive = !newDay[index].isActive;
    setDays(newDay);
  };

  const handleAddHourDay = (index) => {
    const newDay = [...days];
    newDay[index].hours.push(['', '23:59']);
    setDays(newDay);
  };

  const handleCopyTime = (hours, index) => {
    setIsCopied(index);
    setCopyTime([...hours]);
    setTimeout(() => setIsCopied(), 1000);
  };

  const handlePasteTime = (index) => {
    if (copyTime) {
      const newDay = [...days];
      newDay[index].hours = copyTime;
      setCopyTime();
      setDays([...newDay]);
    }
  };

  const handleGenerateTags = () => {
    message.info('Smart Tag generation coming soon');
  };

  const validateForm = () => {
    const newErrors = {
      ...errors,
      errors: {
        name:
          !media.name || !regexInput(media.name)
            ? [{ msg: 'Name is required' }]
            : null,
        description:
          media.description && !regexDescription(media.description)
            ? [{ msg: 'Invalid description' }]
            : null,
        duration:
          !media.duration || media.duration <= 0
            ? [{ msg: 'Duration is required and must be greater than 0' }]
            : null,
        file: !id && !media.file ? [{ msg: 'File is required' }] : null,
        playlist:
          !categoryIds || categoryIds.length === 0
            ? [{ msg: 'Playlist is required' }]
            : null
      },
      message: 'Validation errors!',
    };
    setErrors(newErrors);
    return !Object.values(newErrors.errors).some(Boolean);
  };

  const normalizeDate = (d, isEnd) => {
    if (!d) return null;
    const m = moment.tz(d, SGT_TZ);
    if (allDay) {
      return isEnd
        ? m.endOf('day').utc().toISOString()
        : m.startOf('day').utc().toISOString();
    }
    return m.utc().toISOString();
  };

  const onCreateCategory = (finalCategory) => {
    dispatch(
      createMediaCategory(
        {
          name: finalCategory?.name,
          description: finalCategory?.description,
          siteId,
        },
        () => {
          setIsModalCategoryShow(false);
          dispatch(setMediaCategory({ _id: '', name: '', description: '' }));
          dispatch(fetchMediaCategories({ siteId }));
        }
      )
    );
  };

  const onCreateTag = (finalTag) => {
    dispatch(
      createTag(
        {
          id: tagDraft._id,
          name: finalTag?.name ?? tagDraft.name,
          siteId,
          isRibbon: !!tagDraft.isRibbon,
          isBuilding: !!tagDraft.isBuilding,
          isSmart: !!tagDraft.isSmart,
          ribbonLabel: tagDraft.ribbonLabel,
          logo: tagDraft.logo,
          file: tagDraft.file,
        },
        () => {
          setIsModalTagShow(false);
          dispatch(
            setTag({
              _id: '',
              name: '',
              isRibbon: false,
              isBuilding: false,
              isSmart: false,
              ribbonLabel: '',
              logo: null,
              file: null,
            })
          );
          dispatch(fetchTags({ siteId }));
        }
      )
    );
  };

  const renderAddDropdown = (label, onAdd) => (menu) => (
    <div>
      <div
        onMouseDown={(e) => e.preventDefault()}
        onClick={onAdd}
        style={{
          padding: '8px 12px',
          color: '#1677ff',
          fontWeight: 600,
          cursor: 'pointer',
          background: '#f0f5ff',
          textAlign: 'center',
        }}
      >
        + Add New {label}
      </div>
      {menu}
    </div>
  );

  const handleSubmit = async () => {
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }
    try {
      const daysFilter = days
        .filter((x) => x.isActive)
        .map((x) => ({
          day: x.name,
          hours: x.hours.map((hour) => hour && hour.join('-')),
        }));

      const basePayload = {
        name: media.name,
        description: media.description,
        duration: media.duration,
        file: media.file,
        siteId,
        categoryIds: JSON.stringify(categoryIds),
        start: normalizeDate(media.start, false),
        end: normalizeDate(media.end, true),
        dayMode: allDay ? 'allDay' : 'customDay',
        days: JSON.stringify(allDay ? [] : daysFilter),
        dates: JSON.stringify([]),
        tagIds: JSON.stringify(tags),
        type: media.type || 'all',
        color: media.color ?? '#4CAF50',
      };

      if (id) {
        dispatch(
          modifyMedia({ id, ...basePayload }, async () => {
            history.push(`/${siteId}/information/${siteName}`);
          })
        );
      } else {
        dispatch(
          createMedia({
            uid: userData.id,
            ...basePayload,
          })
        );
      }
    } catch (e) {
      console.error('Error during form submission:', e);
      toastr.error('', 'An error occurred during submission. Please try again.');
    }
  };

  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement && media.duration !== Math.floor(videoElement.duration)) {
      videoElement.addEventListener('loadedmetadata', () => {
        dispatch(
          setMedia({ ...media, duration: Math.floor(videoElement.duration) })
        );
      });
    }
    return () => {
      if (videoElement) {
        videoElement.removeEventListener('loadedmetadata', () => {});
      }
    };
  }, [imagePreviewUrl]); // eslint-disable-line

  const startValue = media.start ? moment.tz(media.start, SGT_TZ) : null;
  const endValue = media.end ? moment.tz(media.end, SGT_TZ) : null;
  const dateFormat = 'DD/MM/YYYY HH:mm';

  return (
    <div className="media-form">
      <form className="form-wrapper">
        <div>
          <div className="form">
            <label className="form-label">
              Name <span className="is-required">*</span>
            </label>
            <Input
              placeholder="Enter media's name"
              name="name"
              type="text"
              value={media.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
            {errors?.errors?.name?.map((n, idx) => (
              <div className="error-message" key={idx}>{n.msg}</div>
            ))}
          </div>

          <div className="form">
            <label className="form-label">
              Playlist <span className="is-required">*</span>
            </label>
            <Select
              mode="multiple"
              placeholder="Select playlist"
              style={{ width: '100%' }}
              value={categoryIds}
              onChange={(value) => {
                setCategoryIds(value);
                handleChange('categoryIds', value);
              }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '')
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              dropdownRender={renderAddDropdown('Playlist', () =>
                setIsModalCategoryShow(true)
              )}
            >
              {(categoriesList || []).map((cat) => (
                <Select.Option key={cat._id} value={cat._id}>
                  {cat.name}
                </Select.Option>
              ))}
            </Select>
            {errors?.errors?.playlist?.map((p, idx) => (
              <div className="error-message" key={idx}>{p.msg}</div>
            ))}
          </div>

          <div className="form">
            <label className="form-label">
              Duration <span className="is-required">*</span>
            </label>
            <div className="input-group">
              <InputNumber
                size="large"
                disabled={
                  (media.file && media.file.type === 'video/mp4') ||
                  (media.fileType === 'video/mp4' && !media.file)
                }
                placeholder="Enter duration"
                onChange={(value) => handleChange('duration', value)}
                value={media.duration}
              />
              <div>Second</div>
            </div>
            {errors?.errors?.duration?.map((d, idx) => (
              <div className="error-message" key={idx}>{d.msg}</div>
            ))}
          </div>

          <div className="form date-range-box date-container">
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <label className="form-label">Start Date</label>
                <DatePicker
                  style={{ width: '100%' }}
                  format={dateFormat}
                  showTime={{ format: 'HH:mm' }}
                  placeholder="DD/MM/YYYY"
                  value={startValue}
                  onChange={(d) =>
                    handleChange(
                      'start',
                      d
                        ? moment
                            .tz(d.format('YYYY-MM-DD HH:mm:ss'), SGT_TZ)
                            .utc()
                            .toISOString()
                        : null
                    )
                  }
                />
                {errors?.errors?.start?.map((s, idx) => (
                  <div className="error-message" key={idx}>{s.msg}</div>
                ))}
              </div>
              <div style={{ flex: 1 }}>
                <label className="form-label">End Date</label>
                <DatePicker
                  style={{ width: '100%' }}
                  format={dateFormat}
                  showTime={{ format: 'HH:mm' }}
                  placeholder="DD/MM/YYYY"
                  value={endValue}
                  onChange={(d) =>
                    handleChange(
                      'end',
                      d
                        ? moment
                            .tz(d.format('YYYY-MM-DD HH:mm:ss'), SGT_TZ)
                            .utc()
                            .toISOString()
                        : null
                    )
                  }
                />
                {errors?.errors?.end?.map((s, idx) => (
                  <div className="error-message" key={idx}>{s.msg}</div>
                ))}
              </div>
            </div>
            <div className="radio-container">
              <div
                className={`radio ${allDay ? 'active' : ''}`}
                onClick={() => setAllDay(true)}
              >
                {allDay && <CheckBullet color="#64D5D2" />}
              </div>
              <div className="radio-title">All Day - Time</div>
              <div
                className={`radio ${!allDay ? 'active' : ''}`}
                onClick={() => setAllDay(false)}
              >
                {!allDay && <CheckBullet color="#64D5D2" />}
              </div>
              <div className="radio-title">Custom Day - Time</div>
            </div>

            {!allDay &&
              days.map((day, index) => (
                <div className="daytime-wrapper" key={index}>
                  <div className="day-container">
                    <div
                      className={`radio ${day.isActive ? 'active' : ''}`}
                      onClick={() => handleActiveDay(index)}
                    >
                      {day.isActive && <CheckBullet color="#64D5D2" />}
                    </div>
                    <div className="radio-title">{day.name}</div>
                  </div>
                  {day.isActive &&
                    day.hours &&
                    day.hours.map((hour, indexHour) => {
                      const hourFrom = hour[0] ? hour[0] : '';
                      const hourTo = hour[1] ? hour[1] : '';
                      return (
                        <div
                          key={`${day.name}-${indexHour}`}
                          className="input-group space"
                        >
                          <InputMask
                            mask="99:99"
                            placeholder="00:00"
                            type="text"
                            value={hourFrom}
                            onChange={(e) =>
                              handleChangeHour('from', index, indexHour, e.target.value)
                            }
                          />
                          <span className="time-range">-</span>
                          <InputMask
                            mask="99:99"
                            placeholder="00:00"
                            type="text"
                            value={hourTo}
                            onChange={(e) =>
                              handleChangeHour('to', index, indexHour, e.target.value)
                            }
                          />
                          <button
                            className="button-link red-single"
                            type="button"
                            onClick={() => handleDeleteHourDay(index, indexHour)}
                          >
                            <TrashOutlineIcon color="#CB3A31" size={20} />
                          </button>
                        </div>
                      );
                    })}
                  {day.isActive && (
                    <div className="time-slot-menu">
                      <button
                        className="button-link primary"
                        type="button"
                        onClick={() => handleAddHourDay(index)}
                      >
                        <PlusIcon color="#64D5D2" size={20} />
                        Time Slot
                      </button>
                      <Popover
                        visible={isCopied === index}
                        content={
                          <p className="copied-popover">
                            <i className="feather-check" />
                            {day.hours.length}{' '}
                            {`Time Slot${day.hours.length > 1 ? 's' : ''} Copied!`}
                          </p>
                        }
                        trigger="click"
                      >
                        <button
                          className="button-link grey"
                          type="button"
                          onClick={() => handleCopyTime(day.hours, index)}
                        >
                          <CopyOutlineIcon size={18} color="#9E9E9E" />
                        </button>
                      </Popover>
                      <button
                        className="button-link grey"
                        type="button"
                        onClick={() => handlePasteTime(index)}
                      >
                        <PasteOutlineIcon size={18} color="#9E9E9E" />
                      </button>
                      <button
                        className="button-link red"
                        type="button"
                        onClick={() => handleDeleteDay(index)}
                      >
                        <TrashOutlineIcon color="#CB3A31" size={20} />
                        Delete All
                      </button>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>

        <div>
          <div className="form">
            <label className="form-label">
              Upload Content <span className="is-required">*</span>
            </label>
            {imagePreviewUrl ? (
              <div className="upload-image-preview">
                <Tooltip placement="top" title="Delete">
                  <button
                    type="button"
                    onClick={handleDeletePreviewImage}
                    className="btn-remove"
                  >
                    <TrashIcon size={18} color="#CB3A31" />
                  </button>
                </Tooltip>
                {(media.file && media.file.type === 'video/mp4') ||
                (media.fileType === 'video/mp4' && !media.file) ? (
                  <video
                    ref={videoRef}
                    controls="controls"
                    preload="metadata"
                    key={imagePreviewUrl}
                  >
                    <source src={imagePreviewUrl} type="video/mp4" />
                  </video>
                ) : (
                  <img
                    className="media-avatar"
                    src={imagePreviewUrl}
                    alt="Media preview"
                  />
                )}
              </div>
            ) : (
              <Upload
                showUploadList={false}
                beforeUpload={onFileChangedHandler}
                style={{ width: '100%' }}
              >
                <div
                  onDragLeave={() => setIsDragged(false)}
                  onDragOver={() => setIsDragged(true)}
                  className={`upload-drag-and-drop ${isDragged ? 'active' : ''}`}
                >
                  <ImportIcon size={22} color="#C2C2C2" />
                  <p className="upload-title">
                    Drag here or{' '}
                    <span className="upload-highlight">Browse</span>
                  </p>
                  <p className="upload-subtitle">
                    File format supported: jpg, png, mp4 (Up to 300MB)
                  </p>
                </div>
              </Upload>
            )}
            {errors?.errors?.file?.map((f, idx) => (
              <div className="error-message" key={idx}>{f.msg}</div>
            ))}
          </div>

          <div className="form">
            <label className="form-label">Description</label>
            <Input.TextArea
              placeholder="Description"
              name="description"
              value={media.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={4}
            />
            {errors?.errors?.description?.map((d, idx) => (
              <div className="error-message" key={idx}>{d.msg}</div>
            ))}
          </div>

          <div className="form">
            <label className="form-label">Tag</label>
            <Select
              mode="multiple"
              placeholder="Add Tag"
              style={{ width: '100%' }}
              value={tags}
              onChange={setTags}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '')
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              dropdownRender={renderAddDropdown('Tag', () =>
                setIsModalTagShow(true)
              )}
            >
              {(tagsList || []).map((t) => (
                <Select.Option key={t._id} value={t._id}>
                  {t.name}
                </Select.Option>
              ))}
            </Select>
            <div
              style={{
                color: '#13B5B1',
                fontWeight: 600,
                marginTop: '8px',
              }}
            >
              Smart Tag
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              Smart Tag automatically analyzes your content and suggests relevant
              tags using AI. This helps improve organization, searchability, and
              consistency without manual effort.
            </div>
            <Button
              onClick={handleGenerateTags}
              style={{
                marginTop: '8px',
                background:
                  'linear-gradient(90deg, #13B5B1 0%, #7C5CFF 100%)',
                color: '#fff',
                border: 'none',
              }}
            >
              Generate Tags
            </Button>
          </div>
        </div>
      </form>

      <div className="button-wrapper">
        {loading ? (
          <button type="button" disabled className="button-primary">
            Loading...
          </button>
        ) : (
          <button type="button" className="btn-primary" onClick={handleSubmit}>
            Submit
          </button>
        )}
      </div>

      <ModalMediaCategoryForm
        visible={isModalCategoryShow}
        onCancel={() => {
          setIsModalCategoryShow(false);
          dispatch(setMediaCategory({ _id: '', name: '', description: '' }));
        }}
        onCreate={onCreateCategory}
        category={categoryDraft || { _id: '', name: '', description: '' }}
        title="Playlist"
        setCategory={(val) => dispatch(setMediaCategory(val))}
        isEditing={false}
      />

      <ModalTagForm
        visible={isModalTagShow}
        onCancel={() => {
          setIsModalTagShow(false);
          dispatch(
            setTag({
              _id: '',
              name: '',
              isRibbon: false,
              isBuilding: false,
              isSmart: false,
              ribbonLabel: '',
              logo: null,
              file: null,
            })
          );
        }}
        onCreate={onCreateTag}
        tag={tagDraft || { _id: '', name: '' }}
        setTag={(val) => dispatch(setTag(val))}
        isEditing={false}
      />
    </div>
  );
}

export default MediaForm;